import { z } from 'zod'
import {
  ALASAN_SELESAI,
  HUBUNGAN,
  LOKASI_PRESISI,
  LOKASI_SUMBER,
  STATUS_KELUARGA,
  STATUS_RUMAH,
} from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }
import {
  akurasiMeterSchema,
  kunciAlamatSchema,
  latSchema,
  lngSchema,
  optionalTextSchema,
  partialDateSchema,
  pendaftaranSchema,
  phoneSchema,
  requiredTextSchema,
  syncShape,
  timestampSchema,
  uuidSchema,
} from './kolom'

/** `keluarga` domain columns (spec 8.3): what a form, an import row or a creation op supplies. */
export const keluargaSchema = z.object({
  lingkungan_id: uuidSchema,
  nomor_kk_katolik: requiredTextSchema,
  nomor_kk_pemerintah: z
    .string()
    .regex(/^\d{16}$/, ui.validasi.nomorKk16)
    .nullish(),
  /** Kode keluarga (schema 14): the parish's own code, free text, trimmed. */
  kode_keluarga: optionalTextSchema,
  kepala_keluarga_id: uuidSchema.nullish(),
  alamat: optionalTextSchema,
  rt_rw: optionalTextSchema,
  kampung_desa: optionalTextSchema,
  kecamatan: optionalTextSchema,
  kabupaten_kota: optionalTextSchema,
  provinsi: optionalTextSchema,
  kode_pos: z
    .string()
    .regex(/^\d{5}$/, ui.validasi.kodePos)
    .nullish(),
  tanggal_kk: partialDateSchema.nullish(),
  telepon: phoneSchema.nullish(),
  /** Status rumah tinggal (schema 15). */
  status_rumah: z.enum(STATUS_RUMAH).nullish(),
  /** The home's location (schema 16, Peta umat; ../peta/lokasi writes them together). */
  lat: latSchema.nullish(),
  lng: lngSchema.nullish(),
  lokasi_sumber: z.enum(LOKASI_SUMBER).nullish(),
  lokasi_presisi: z.enum(LOKASI_PRESISI).nullish(),
  lokasi_akurasi_m: akurasiMeterSchema.nullish(),
  lokasi_diubah_pada: timestampSchema.nullish(),
  /** The key of the address the location answers (review I1). */
  lokasi_alamat_kunci: kunciAlamatSchema.nullish(),
  status: z.enum(STATUS_KELUARGA).default('aktif'),
  /** Pindah ke keuskupan lain (schema 13, ../pindahKeuskupan). */
  pindah_keuskupan: optionalTextSchema,
  pindah_paroki: optionalTextSchema,
  pindah_tanggal: partialDateSchema.nullish(),
  pendaftaran: pendaftaranSchema.nullish(),
  tanggal_mulai: partialDateSchema.nullish(),
  catatan: optionalTextSchema,
  /** Hapus keluarga (schema 17, ../hapusData): the reason, as typed. */
  alasan_hapus: optionalTextSchema,
})
export type Keluarga = z.infer<typeof keluargaSchema>

/** A full stored row including the sync columns. */
export const keluargaRowSchema = keluargaSchema.extend(syncShape)
export type KeluargaRow = z.infer<typeof keluargaRowSchema>

/** `op.fields` for this table: any subset of columns, nothing unknown (spec 9.5 step 4). */
export const keluargaFieldsSchema = z.strictObject(
  keluargaRowSchema.omit({ _hlc: true }).partial().shape,
)

/** `anggota_keluarga`: membership history (spec 8.3). */
export const anggotaKeluargaSchema = z.object({
  lingkungan_id: uuidSchema.nullish(),
  umat_id: uuidSchema,
  keluarga_id: uuidSchema,
  hubungan: z.enum(HUBUNGAN),
  mulai: partialDateSchema.nullish(),
  selesai: partialDateSchema.nullish(),
  alasan_selesai: z.enum(ALASAN_SELESAI).nullish(),
})
export type AnggotaKeluarga = z.infer<typeof anggotaKeluargaSchema>
export const anggotaKeluargaRowSchema = anggotaKeluargaSchema.extend(syncShape)
export type AnggotaKeluargaRow = z.infer<typeof anggotaKeluargaRowSchema>
export const anggotaKeluargaFieldsSchema = z.strictObject(
  anggotaKeluargaRowSchema.omit({ _hlc: true }).partial().shape,
)
